import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleDot, Circle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ROLE_INFO } from "@/context/AuthContext";
import { Button, Card, Chip, Field, Input, Page, PageHeader, Switch, StickyFooter, Loading } from "@/components/kit";

export default function UserForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: users = [], isLoading } = useQuery({ queryKey: ["users"], queryFn: () => api("/users") });
  const { data: units = [] } = useQuery({ queryKey: ["units"], queryFn: () => api("/units") });
  const { data: sectors = [] } = useQuery({ queryKey: ["sectors"], queryFn: () => api("/sectors") });
  const [f, setF] = useState({ name: "", email: "", password: "", role: "operador", unit_ids: [], sector_ids: [], active: true });
  const [err, setErr] = useState("");
  const [confirm, setConfirm] = useState(false);
  const existing = users.find((u) => u.id === id);
  useEffect(() => { if (existing) setF({ ...existing, password: "" }); }, [existing]);
  useEffect(() => { if (!id && units.length && !f.unit_ids.length) setF((x) => ({ ...x, unit_ids: [units[0].id] })); }, [units, id, f.unit_ids.length]);
  const toggle = (k, v) => setF({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] });
  const done = (m) => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success(m); nav("/users"); };
  const save = useMutation({
    mutationFn: () => api(id ? `/users/${id}` : "/users", { method: id ? "PUT" : "POST", body: { ...f, unit_ids: f.role === "admin" ? units.map((u) => u.id) : f.unit_ids } }),
    onSuccess: () => done(id ? "Usuário atualizado" : "Usuário criado"),
    onError: (e) => setErr(e.message),
  });
  const del = useMutation({ mutationFn: () => api(`/users/${id}`, { method: "DELETE" }), onSuccess: () => done("Usuário excluído"), onError: (e) => toast.error(e.message) });
  const submit = () => {
    setErr("");
    if (!f.name.trim()) return setErr("Informe o nome");
    if (!/^\S+@\S+\.\S+$/.test(f.email)) return setErr("Informe um e-mail válido");
    if (f.role !== "admin" && !f.unit_ids.length) return setErr("Selecione ao menos uma unidade");
    if ((!id || f.password) && f.password.length < 6) return setErr("A senha deve ter ao menos 6 caracteres");
    save.mutate();
  };
  if (id && isLoading) return <Loading />;
  return (
    <Page narrow>
      <PageHeader title={id ? "Editar usuário" : "Novo usuário"} close />
      <Card className="flex flex-col gap-5">
        <Field label="Nome"><Input placeholder="Nome completo" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name-input" /></Field>
        <Field label="E-mail"><Input type="email" placeholder="usuario@restaurante.com" disabled={!!id} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email-input" /></Field>
        <Field label={id ? "Nova senha (opcional)" : "Senha"}><Input type="password" placeholder="Mínimo 6 caracteres" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password-input" /></Field>
        <div className="flex flex-col gap-2">
          <span className="label">Nível de acesso</span>
          {Object.entries(ROLE_INFO).map(([k, r]) => (
            <button key={k} type="button" className="flex gap-3 items-start text-left rounded-xl p-3 border" style={{ borderColor: f.role === k ? "var(--brand)" : "var(--border)", background: f.role === k ? "var(--brand-tint)" : "transparent" }} onClick={() => setF({ ...f, role: k })} data-testid={`user-role-${k}`}>
              {f.role === k ? <CircleDot size={18} style={{ color: "var(--brand)" }} /> : <Circle size={18} className="muted" />}
              <div><div className="h3">{r.label}</div><div className="caption">{r.desc}</div></div>
            </button>
          ))}
        </div>
        {f.role === "operador" && (
          <div className="flex flex-col gap-2" data-testid="user-sectors-section">
            <span className="label">Setores de contagem (unidade atual)</span>
            <span className="caption">Sem setor marcado, o operador conta todos os produtos.</span>
            <div className="flex flex-wrap gap-2">{sectors.map((s) => <Chip key={s.id} active={f.sector_ids.includes(s.id)} onClick={() => toggle("sector_ids", s.id)} data-testid={`user-sector-${s.id}`}>{s.name}</Chip>)}</div>
          </div>
        )}
        <div className="flex flex-col gap-2" data-testid="user-units-section">
          <span className="label">Unidades com acesso</span>
          {f.role === "admin" ? <span className="caption">Administradores acessam todas as unidades.</span> : (
            <div className="flex flex-wrap gap-2">{units.map((u) => <Chip key={u.id} active={f.unit_ids.includes(u.id)} onClick={() => toggle("unit_ids", u.id)} data-testid={`user-unit-${u.id}`}>{u.name}</Chip>)}</div>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex-1"><div className="label">Usuário ativo</div><div className="caption">Usuários inativos não conseguem entrar.</div></div>
          <Switch on={f.active} onChange={(v) => setF({ ...f, active: v })} data-testid="user-active-switch" />
        </div>
        {err && <span className="caption" style={{ color: "var(--danger)" }} data-testid="user-form-error">{err}</span>}
        {id && (confirm ? (
          <div className="flex gap-2"><Button variant="secondary" size="sm" onClick={() => setConfirm(false)} data-testid="cancel-delete-user">Cancelar</Button><Button variant="danger" size="sm" loading={del.isPending} onClick={() => del.mutate()} data-testid="confirm-delete-user">Confirmar</Button></div>
        ) : <Button variant="ghost" size="sm" icon={Trash2} className="self-start" onClick={() => setConfirm(true)} data-testid="delete-user-button">Excluir usuário</Button>)}
      </Card>
      <StickyFooter><Button loading={save.isPending} onClick={submit} data-testid="save-user-button">Salvar usuário</Button></StickyFooter>
    </Page>
  );
}
